import React from "react";
import PropTypes from "prop-types";
import { CollectionName } from "../../Atoms/CollectionName";
import { Link } from "@quintype/components";
import { clientWidth, generateNavigateSlug, navigateTo } from "../../../utils/utils";
import { getCategoryAndSubCategory } from "../../../../ga-utils";
import { getTemplateName } from "../../../../components/utils";
import "../MagazineHeaderCard/magazine-cards.m.css";
import { StateProvider } from "../../SharedContext";
import { StoryCard } from "../../Molecules/StoryCard";
import { HeroImage } from "../../Atoms/HeroImage";
import { StorycardContent } from "../../Molecules/StorycardContent";
import { Headline } from "../../Atoms/Headline";
import { AuthorWithTime } from "../../Atoms/AuthorWithTimestamp";
import { MagazineCoverImageCard } from "../../Atoms/MagazineCoverImage";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import { getMagazinesTimeStamp } from "../../../utils/timestamp-utils";
import { LoadmoreButton } from "../../Atoms/Loadmore";

const MagazineWidget = ({ collection = {}, config = {}, pageType }) => {
  const { "created-at": createdAt, "collection-date": issueDate, summary, items, metadata = {} } = collection;
  const {
    theme = "",
    footerButton = "",
    border,
    localizationConfig = {},
    navigate = true,
    magazineSlug = "",
    rowTitle,
    showRowTitle,
    showBottomReadMore,
    showTitleReadMore,
  } = config;

  const handleClick = (position) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: position,
        section_name: collection?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const dispatch = useDispatch();
  const qtConfig = useSelector((state) => get(state, ["qt", "config"], {}));

  const updatedMagazineSlug =
    magazineSlug || get(metadata, ["entities", "collectionEntities", "magazine", 0, "slug"], "");
  const updatedConfig = { ...config, magazineSlug: updatedMagazineSlug, customUrlPath: updatedMagazineSlug };

  const url = generateNavigateSlug(collection, {
    ...qtConfig,
    ...updatedConfig,
  });
  const sliceValue = 4;
  const isMobile = clientWidth("mobile");
  const timeStampConfig = {
    isUpperCase: true,
    disableMeridiem: true,
  };
  const date = issueDate || createdAt;

  const storyCards = (items) => {
    return (
      <div styleName="story-cards">
        {items.slice(0, sliceValue).map((storyItems, index) => {
          const story = storyItems.story;
          const borderBottom = () => {
            const lastStoryIndex = 3;
            const lastTwoStoriesIndex = [2, 3];
            if (isMobile && !lastStoryIndex === index) {
              return "bottom";
            }
            if (!lastTwoStoriesIndex.includes(index)) {
              return "bottom";
            }
          };

          const borderSettings = border === "borderBottom" ? borderBottom() : "";
          return (
            <>
              <div onClick={(e) => { e.stopPropagation(); handleClick(index + 2); }}>
                <StoryCard story={story} border={borderSettings} isHorizontal config={config}>
                  <HeroImage story={story} isHorizontal aspectRatio={[[4, 3]]} />
                  <StorycardContent story={story} config={config}>
                    <Headline story={story} premiumStoryIconConfig={config} />
                    {/* <AuthorWithTime
                      config={localizationConfig}
                      story={story}
                      showMultipleAuthor={false}
                      showTimeStamp={false}
                    /> */}
                  </StorycardContent>
                </StoryCard>
              </div>
            </>
          );
        })}
      </div>
    );
  };

  const bottomCard = () => {
    return (
      <div styleName="widget">
        {storyCards(items)}
        {showBottomReadMore && (
          <div onClick={(e) => { e.stopPropagation(); handleClick(-1); }}>
            <Link href={`/magazine/${collection.slug}`} disableAjaxLinks={true}>
              <div className="arr--button">Read More</div>
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="full-width-with-padding arrow-component"
      style={{ backgroundColor: theme }}
      data-test-id="magazine-widget"
      styleName="container"
    >
      {showRowTitle && (
        <div styleName="collection-title-container" className="collection-title-container">
          <div onClick={(e) => { e.stopPropagation(); handleClick(-2); }}>
            <Link aria-label="magazine-widget" href={`/magazine/${collection.slug}`} disableAjaxLinks={true}>
              <CollectionName collection={{ name: config?.rowTitle }} navigate={false} />
            </Link>
          </div>
          {showTitleReadMore && (
            <div onClick={(e) => { e.stopPropagation(); handleClick(-1); }}>
              <LoadmoreButton
              config={updatedConfig}
              collection={collection}
              qtConfig={qtConfig}
              isBtnWithArrow={true}
            />
            </div>
          )}
        </div>
      )}
      <div className="arr-magazine-widget" styleName="magazine-header widget-button">
        <div onClick={(e) => { e.stopPropagation(); handleClick(1); }}>
          <MagazineCoverImageCard collection={collection} config={updatedConfig} isHomepage={true} />
        </div>
        <div styleName="content">
          <div styleName="cover-collection-details">
            <Link href={`/magazine/${collection.slug}`} aria-label="magazine-widget-details" disableAjaxLinks={true}>
            <div styleName={`time `} data-test-id="widget-date" className="time">
              {getMagazinesTimeStamp(date)}
            </div>
            <CollectionName
              collection={collection}
              config={config}
              navigate={navigate}
              customSlug={`/magazine/${collection.slug}`}
            />
            <div styleName={`summary`} className="summary" data-test-id="widget-summary">
              {summary}
            </div>
            </Link>
          </div>
          {!showBottomReadMore && bottomCard()}
        </div>
        {showBottomReadMore && bottomCard()}
      </div>
    </div>
  );
};

MagazineWidget.propTypes = {
  collection: PropTypes.shape({
    "created-at": PropTypes.number,
    summary: PropTypes.string,
    items: PropTypes.array,
  }),
  config: PropTypes.shape({
    theme: PropTypes.string,
    footerButton: PropTypes.string,
    border: PropTypes.string,
  }),
  pageType: PropTypes.string,
};

export default StateProvider(MagazineWidget);
